/**
 * Helpers for showing server-authoritative prices in the cart.
 *
 * The cart keeps each item's price from the moment it was added (persisted in
 * localStorage), so it goes stale whenever the store changes a price. The
 * server prices every order itself (POST /customer/cart/quote, and again when
 * the order is placed); these helpers bring the cart's display in line with
 * that, so the total a customer sees is the total they pay.
 *
 * Kept free of React and react-router so they can be unit-tested.
 */

const lineKey = (productId, variantLabel) => `${productId}__${variantLabel || ''}`;

/**
 * Returns `items` with price, mrp and total taken from the server quote's
 * lines (matched by product + variant), plus the promotion (if any) that the
 * price includes. Returns the SAME array when nothing changed, so callers can
 * skip a state update and avoid re-render loops.
 */
export const applyQuoteToItems = (items, lines) => {
  if (!Array.isArray(items) || !Array.isArray(lines) || lines.length === 0) return items;
  const byKey = new Map(lines.map((l) => [lineKey(l.product_id, l.variant_label), l]));
  let changed = false;
  const next = items.map((item) => {
    const line = byKey.get(lineKey(item.product_id, item.variant_label));
    if (!line || typeof line.price !== 'number') return item;
    const mrp = typeof line.mrp === 'number' ? line.mrp : item.mrp;
    const promotionName = line.promotion_name || null;
    const promotionLabel = line.promotion_label || null;
    if (line.price === item.price && mrp === item.mrp
        && promotionName === (item.promotion_name || null) && promotionLabel === (item.promotion_label || null)) return item;
    changed = true;
    return {
      ...item, price: line.price, mrp, total: line.price * item.quantity,
      promotion_name: promotionName, promotion_label: promotionLabel,
    };
  });
  return changed ? next : items;
};

/**
 * Identifies everything a quote depends on — deliberately NOT the prices, so
 * refreshing prices from a quote never triggers another quote.
 */
export const cartQuoteKey = (items, couponCode, fulfillmentType, address) => JSON.stringify([
  (items || []).map((i) => [i.product_id, i.variant_label || null, i.quantity]),
  couponCode || null,
  fulfillmentType || null,
  address ? [address.latitude ?? null, address.longitude ?? null, address.pincode ?? null] : null,
]);

/**
 * The 409 the server returns when the checkout displayed a different total
 * than the server computed. Nothing was created; `quote` holds the correct
 * figures to show before the customer confirms.
 */
export const priceChangedQuote = (error) => {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 409 && detail && detail.code === 'PRICE_CHANGED' && detail.quote) {
    return { message: detail.message, quote: detail.quote };
  }
  return null;
};
